import axios from "axios";
import { store } from "./store";

export const BASE_URL = `${process.env.REACT_APP_BACKEND_URL}`;

const http = axios.create({
  baseURL: BASE_URL,
  headers: {
    "Content-type": "application/json",
  },
  // Required so the browser sends/receives the httpOnly refresh-token cookie.
  withCredentials: true,
});

http.interceptors.request.use((config) => {
  const token = store.getState().user?.token;
  if (token && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Requests that should never trigger a silent-refresh attempt (the refresh
// call itself, and login/register, whose 401/400s mean bad credentials, not
// an expired session).
const REFRESH_EXEMPT = ["/users/refresh", "/users/login", "/users/register"];

let isRefreshing = false;
let pendingQueue = [];

function flushQueue(error, token) {
  pendingQueue.forEach(({ resolve, reject }) => (error ? reject(error) : resolve(token)));
  pendingQueue = [];
}

http.interceptors.response.use(
  (response) => response,
  async (error) => {
    const { response, config: originalRequest } = error;
    const isRefreshExempt = REFRESH_EXEMPT.some((url) => originalRequest?.url?.includes(url));

    if (!response || response.status !== 401 || !originalRequest || originalRequest._retry || isRefreshExempt) {
      return Promise.reject(error);
    }

    if (isRefreshing) {
      return new Promise((resolve, reject) => pendingQueue.push({ resolve, reject })).then((token) => {
        originalRequest._retry = true;
        originalRequest.headers.Authorization = `Bearer ${token}`;
        return http(originalRequest);
      });
    }

    originalRequest._retry = true;
    isRefreshing = true;

    try {
      const { data } = await http.post("/users/refresh");
      store.dispatch({ type: "REFRESH_TOKEN", payload: { token: data.token } });
      flushQueue(null, data.token);
      originalRequest.headers.Authorization = `Bearer ${data.token}`;
      return http(originalRequest);
    } catch (refreshError) {
      flushQueue(refreshError, null);
      store.dispatch({ type: "LOGOUT", payload: null });
      if (typeof window !== "undefined" && window.location.pathname !== "/login") {
        window.location.href = "/login";
      }
      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  }
);

export default http;
